/**
 * El reporte al cliente, en la fila de pestañas: un menú al lado de «Enviar
 * al cliente» (Eduardo, 2026-09-06: "que ese botón sea botón y dropdown,
 * donde se puede elegir y esté lo de cadencia y la información actual").
 *
 * Arriba lo que se puede hacer —preparar, abrir, enviar—, después la cadencia
 * con la actual marcada, y al final la información: último enviado, período,
 * el preparado. Las líneas de información van como ítems deshabilitados: se
 * leen, no se aprietan. Enviar pregunta: sale por correo de verdad y cierra
 * el período (REGLA #24).
 */
import { useTranslation } from 'react-i18next';
import { ActionDropdown, type ActionMenuItem } from '../../ui/Dropdown.js';
import { useConfirm } from '../../ui/ConfirmDialog.js';
import { useToast } from '../../ui/Toast.js';
import type { Cadencia } from '../../../hooks/useMantenimiento.js';
import { usePeriodo, useFijarCadencia, usePrepararReporte, useEnviarReporte } from '../../../hooks/useMantenimiento.js';

const CADENCIAS: Cadencia[] = ['apagado', 'semanal', 'quincenal', 'mensual'];

interface Props {
  projectId: number;
  /** «Enviar al cliente» (el correo del diseño) vive adentro del menú: Eduardo (2026-09-06). */
  enviarAlCliente: () => void;
  enviando: boolean;
}

export function MenuDelReporte({ projectId, enviarAlCliente, enviando }: Props) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { data } = usePeriodo(projectId, true);
  const fijar = useFijarCadencia(projectId);
  const preparar = usePrepararReporte(projectId);
  const enviar = useEnviarReporte(projectId);
  const { confirm, dialog } = useConfirm();
  const r = 'projects.maintenance.reports';

  const fmt = (iso: string) => new Date(iso).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' });
  const fmtDia = (f: string) => new Date(`${f.slice(0, 10)}T12:00:00Z`).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' });
  const fallo = (e: unknown): void => { toast.error(e instanceof Error ? e.message : t('common.error')); };

  async function mandar(reportId: number): Promise<void> {
    const ok = await confirm({
      title: t(`${r}.sendTitle`),
      description: t(`${r}.sendBody`),
      confirmLabel: t(`${r}.send`),
      tone: 'warning',
    });
    if (!ok) return;
    enviar.mutate(reportId, { onError: fallo, onSuccess: () => { toast.success(t(`${r}.sent`)); } });
  }

  const preparado = data?.reportePreparado ?? null;
  const vacio = data?.datos.vacio === true;
  const cadencia = data?.cadenciaActual ?? 'apagado';
  const rec = data?.cadenciaRecomendada ?? null;

  const items: ActionMenuItem[] = [
    { type: 'action', label: t('projects.design.tab.step2.sendClient'), disabled: enviando, onClick: enviarAlCliente },
    { type: 'separator' },
    preparado
      ? { type: 'action', label: t(`${r}.open`), onClick: () => { window.open(preparado.url, '_blank', 'noopener'); } }
      : { type: 'action', label: t(`${r}.prepare`), disabled: vacio || preparar.isPending, onClick: () => { preparar.mutate(undefined, { onError: fallo }); } },
    ...(preparado
      ? [
        { type: 'action' as const, label: t(`${r}.send`), disabled: enviar.isPending, onClick: () => { void mandar(preparado.reportId); } },
        { type: 'action' as const, label: t(`${r}.prepareAgain`), disabled: preparar.isPending, onClick: () => { preparar.mutate(undefined, { onError: fallo }); } },
      ]
      : []),
    { type: 'separator' },
    ...CADENCIAS.map((c): ActionMenuItem => ({
      type: 'action',
      label: `${c === cadencia ? '✓ ' : '   '}${t(`${r}.cadenceOption`, { cadencia: t(`${r}.${c}`) })}${rec && rec.cadencia === c && c !== cadencia ? ` · ${t(`${r}.recommendedShort`)}` : ''}`,
      disabled: fijar.isPending,
      onClick: () => { if (c !== cadencia) fijar.mutate(c, { onError: fallo }); },
    })),
    { type: 'separator' },
    { type: 'action', label: t(`${r}.lastSentLine`, { fecha: data?.ultimoReporteEnviado ? fmt(data.ultimoReporteEnviado) : t(`${r}.never`) }), disabled: true, onClick: () => {} },
    ...(data
      ? [{
        type: 'action' as const,
        label: data.datos.ventana.recienEmpieza
          ? t(`${r}.windowNewShort`, { desde: fmtDia(data.datos.ventana.desde) })
          : t(`${r}.window`, { desde: fmtDia(data.datos.ventana.desde), hasta: fmtDia(data.datos.ventana.hasta) }),
        disabled: true, onClick: () => {},
      }]
      : []),
    ...(preparado
      ? [{ type: 'action' as const, label: t(`${r}.preparedLine`, { fecha: fmt(preparado.creadoEn) }), disabled: true, onClick: () => {} }]
      : vacio
        ? [{ type: 'action' as const, label: t(`${r}.emptyShort`), disabled: true, onClick: () => {} }]
        : []),
  ];

  return (
    <>
      <ActionDropdown label={preparado ? t(`${r}.menuReady`) : t(`${r}.menu`)} items={items} size="md" tone={preparado ? 'cyan' : 'muted'} />
      {dialog}
    </>
  );
}
